import React, { FC, ReactNode, useEffect, useRef, useState } from 'react';
import Icon from '@common/assets';
import BackgroundArea from '@components/profile/profileOverlay/overlays/common/backgroundArea';
import ExitModal from '@components/profile/profileOverlay/exitModal';
import EduList from '@components/profile/profileOverlay/overlays/edu/EduList';
import EduForm from '@components/profile/profileOverlay/overlays/edu/eduForm';
import EduDeleteList from '@components/profile/profileOverlay/overlays/edu/EduDeleteList';
import useProfileEducation from '@repository/profiles/useProfileEducation';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { EducationVO } from '@domain/vo/profile/ProfileEducationVO';
import Colors from '@domain/constant/colors';

import classnames from 'classnames/bind';
import styles from './edu.module.scss';
const cx = classnames.bind(styles);

export type EduStateType = 'LIST' | 'FORM' | 'DELETE';

interface IEduOverlay {
  visible: boolean;
  onClose: () => void;
}

const EduOverlay: FC<IEduOverlay> = ({ visible, onClose }) => {
  const fromContentsRef = useRef<ReactNode>(null);

  const [eduState, setEduState] = useState<EduStateType>('LIST');
  const [exitModalVisible, setExitModalVisible] = useState(false);
  const [directExitModalVisible, setDirectExitModalVisible] = useState(false);

  const [education, setEducation] = useState<EducationVO | null>(null);
  const [educationIndex, setEducationIndex] = useState<number | null>(null);
  const { data: userInfo } = useUserInfo();
  const { data: profileEducation, refetch } = useProfileEducation(!!userInfo?.id && visible);

  const openExitModal = () => {
    setExitModalVisible(true);
  };

  useEffect(() => {
    if (!visible) init();
  }, [visible]);

  const init = () => {
    setEduState('LIST');
  };

  const onCloseOverlay = () => {
    if (eduState === 'FORM') {
      // @ts-ignore
      const isDirty = fromContentsRef.current?.getIsDirty() ?? false;
      if (isDirty) {
        setDirectExitModalVisible(true);
        return;
      }
    }

    onClose();
  };

  const getEduTitle = () => {
    switch (eduState) {
      // 학력 입력 영역
      case 'FORM':
        return (
          <div className={cx('overlayTitleArea')}>
            <strong className={cx('overlayTitle')}>나의 학력 정보를 등록해주세요.</strong>
            <p className={cx('desc')}>
              <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_70} />
              학력 정보는 기업에 따라 선택적으로 활용돼요.
            </p>
          </div>
        );

      // 학력 리스트 / 학력 삭제 영역
      default:
        return (
          <div className={cx('overlayTitleArea')}>
            <strong className={cx('overlayTitle')}>등록된 학력 정보를 확인해주세요.</strong>
            <p className={cx('desc')}>
              <Icon name="informationLight" width={20} height={20} fill={Colors.C_COOL_GRAY_70} />
              최근 활동 순으로 자동 정렬돼요.
            </p>
          </div>
        );
    }
  };

  const getEduContents = () => {
    switch (eduState) {
      // 학력 리스트 영역
      case 'LIST':
        return (
          <EduList
            profileEducation={profileEducation}
            setEduState={setEduState}
            setEducation={setEducation}
            setEducationIndex={setEducationIndex}
            onClose={onClose}
          />
        );

      // 학력 입력 영역
      case 'FORM':
        return (
          <EduForm
            refetch={refetch}
            profileEducation={profileEducation}
            education={education}
            educationIndex={educationIndex}
            setEduState={setEduState}
            openExitModal={openExitModal}
            ref={fromContentsRef}
          />
        );

      // 학력 리스트 삭제 영역
      case 'DELETE':
        return (
          <EduDeleteList
            refetch={refetch}
            profileEducation={profileEducation}
            setEduState={setEduState}
            openExitModal={openExitModal}
          />
        );
    }
  };

  return (
    <>
      <BackgroundArea visible={visible} onClickArea={onCloseOverlay} />

      <div className={cx('overlayArea', { on: visible })}>
        <div className={cx('overlayInner')}>
          {getEduTitle()}

          {getEduContents()}
        </div>

        {/* 나가기 모달 영역 */}
        <ExitModal
          visible={exitModalVisible}
          onClose={() => setExitModalVisible(false)}
          onExit={() => setEduState('LIST')}
        />
        {/* 오버레이 나가기 모달 영역 */}
        <ExitModal visible={directExitModalVisible} onClose={() => setDirectExitModalVisible(false)} onExit={onClose} />
      </div>
    </>
  );
};

export default EduOverlay;
